/**
 * <FacultyInsights> — /faculty/insights
 *
 * Two views of the same aggregation:
 *   1. Topic Health — ranked list of decks by mastery, weakest first,
 *      with a 5-strategy accuracy split (standard/confused/bridge/
 *      cloze/feynman) so the faculty sees whether students are failing
 *      on recall (comprehension) vs bridge (connection) etc.
 *   2. Struggling students — anyone under threshold (default 60%).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STUDY_THEME, paperBackground, CARD_TYPE_META } from '../components/study/studyTheme';
import { getFacultyTopicHealth, getFacultyStudents, nudgeStudent } from '../lib/studyApi';

const cardStyle = {
  background: 'white', borderRadius: 12, padding: 20,
  boxShadow: STUDY_THEME.shadow, border: `1px solid ${STUDY_THEME.line}`,
  marginBottom: 14,
};

const tabBtn = (active) => ({
  padding: '10px 20px', fontSize: 14, fontWeight: 700,
  border: 'none', cursor: 'pointer', fontFamily: 'inherit',
  background: active ? STUDY_THEME.gold : 'transparent',
  color: active ? STUDY_THEME.ink : STUDY_THEME.inkSoft,
  borderBottom: active ? `3px solid ${STUDY_THEME.goldDark}` : '3px solid transparent',
});

function TypeBar({ by_type }) {
  const order = ['standard', 'confused', 'bridge', 'cloze', 'feynman'];
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
      {order.filter((t) => by_type[t]).map((t) => {
        const v = by_type[t];
        const meta = CARD_TYPE_META[t] || CARD_TYPE_META.standard;
        const color = v.accuracy >= 75 ? '#16A34A'
                    : v.accuracy >= 55 ? STUDY_THEME.goldDark
                    : '#DC2626';
        return (
          <div key={t} data-testid={`type-bar-${t}`} style={{
            padding: '5px 10px', borderRadius: 6, background: '#F8F9FB',
            border: `1px solid ${STUDY_THEME.line}`, fontSize: 11,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ color: meta.accent, fontWeight: 700 }}>{meta.emoji}</span>
            <span style={{ color: STUDY_THEME.inkSoft }}>{meta.label}</span>
            <span style={{ color, fontWeight: 700 }}>{v.accuracy.toFixed(0)}%</span>
            <span style={{ color: STUDY_THEME.muted }}>({v.total})</span>
          </div>
        );
      })}
    </div>
  );
}

export default function FacultyInsights() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('topics'); // topics | students
  const [topics, setTopics] = useState(null);
  const [students, setStudents] = useState(null);
  const [threshold, setThreshold] = useState(60);
  const [err, setErr] = useState('');
  const [nudging, setNudging] = useState('');
  const [nudgeToast, setNudgeToast] = useState('');

  useEffect(() => {
    getFacultyTopicHealth().then(setTopics).catch((e) =>
      setErr(e?.response?.data?.detail || e.message || 'Load failed'));
  }, []);

  useEffect(() => {
    if (tab === 'students') {
      getFacultyStudents(threshold).then(setStudents).catch((e) =>
        setErr(e?.response?.data?.detail || e.message || 'Load failed'));
    }
  }, [tab, threshold]);

  const sendNudge = async (row) => {
    setNudging(row.email);
    setNudgeToast('');
    try {
      const resp = await nudgeStudent({
        student_email: row.email,
        weakest_mastery: row.recall_mastery,
        weakest_card_front: null, // topic-specific gap could be plugged in later
      });
      setNudgeToast(`✅ Sent to ${row.name} at ${new Date(resp.sent_at).toLocaleTimeString()}`);
    } catch (e) {
      setNudgeToast(`❌ ${e?.response?.data?.detail || e.message}`);
    } finally {
      setNudging('');
      setTimeout(() => setNudgeToast(''), 5000);
    }
  };

  const overallMastery = useMemo(() => {
    if (!topics || !topics.decks.length) return null;
    return (topics.decks.reduce((s, d) => s + d.mastery, 0) / topics.decks.length).toFixed(1);
  }, [topics]);

  return (
    <div style={paperBackground()} data-testid="faculty-insights-page">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{
              fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase',
              fontWeight: 700, color: STUDY_THEME.goldDeep,
            }}>Institute of Analytics · Faculty</div>
            <h1 style={{
              fontFamily: '"Playfair Display", serif', fontSize: 40,
              margin: '2px 0 4px',
            }}>📊 Recall/Connect Insights</h1>
            <p style={{ color: STUDY_THEME.inkSoft, margin: 0, fontSize: 15 }}>
              How students are retaining, connecting, and where content
              needs attention. Aggregated from every review and duel.
            </p>
          </div>
          <button
            onClick={() => navigate('/teacher')}
            data-testid="faculty-back-btn"
            style={{
              padding: '8px 14px', fontSize: 13, fontWeight: 600,
              background: 'white', color: STUDY_THEME.inkSoft,
              border: `1px solid ${STUDY_THEME.line}`, borderRadius: 6,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >← Dashboard</button>
        </div>

        {topics && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 12, marginBottom: 24,
          }}>
            {[
              ['Decks tracked', topics.decks.length],
              ['Students active', topics.total_students],
              ['Total reviews', topics.total_reviews],
              ['Avg mastery', overallMastery != null ? `${overallMastery}%` : '—'],
            ].map(([label, val]) => (
              <div key={label} style={{ ...cardStyle, textAlign: 'center', marginBottom: 0 }}>
                <div style={{
                  fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                  fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 6,
                }}>{label}</div>
                <div style={{
                  fontFamily: '"Playfair Display", serif', fontSize: 34,
                  fontWeight: 700, color: STUDY_THEME.ink, lineHeight: 1,
                }}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {err && (
          <div data-testid="faculty-error" style={{
            padding: 14, borderRadius: 10, background: '#FEE2E2',
            color: '#991B1B', marginBottom: 16,
          }}>{err}</div>
        )}

        <div style={{ display: 'flex', borderBottom: `1px solid ${STUDY_THEME.line}`, marginBottom: 20 }}>
          <button
            onClick={() => setTab('topics')}
            data-testid="tab-topics"
            style={tabBtn(tab === 'topics')}
          >Topic Health</button>
          <button
            onClick={() => setTab('students')}
            data-testid="tab-students"
            style={tabBtn(tab === 'students')}
          >Struggling Students</button>
        </div>

        {tab === 'topics' && (
          topics ? (
            topics.decks.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: 'center', color: STUDY_THEME.muted }}>
                No student reviews yet. Once students start reviewing,
                topic health will appear here.
              </div>
            ) : topics.decks.map((d) => {
              const barColor = d.mastery >= 70 ? '#16A34A'
                             : d.mastery >= 50 ? STUDY_THEME.goldDark
                             : '#DC2626';
              return (
                <div key={d.deck_id} style={cardStyle}
                     data-testid={`topic-row-${d.deck_id}`}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', gap: 20,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontFamily: '"Playfair Display", serif',
                        fontSize: 22, fontWeight: 600, color: STUDY_THEME.ink,
                      }}>{d.name}</div>
                      <div style={{ fontSize: 13, color: STUDY_THEME.muted, marginTop: 4 }}>
                        {d.students_touched} student{d.students_touched === 1 ? '' : 's'} ·
                        {' '}{d.reviews} reviews
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', minWidth: 100 }}>
                      <div style={{
                        fontFamily: '"Playfair Display", serif', fontSize: 34,
                        fontWeight: 700, color: barColor, lineHeight: 1,
                      }}>{d.mastery.toFixed(0)}%</div>
                      <div style={{ fontSize: 11, color: STUDY_THEME.muted, letterSpacing: 1 }}>
                        mastery
                      </div>
                    </div>
                  </div>
                  <TypeBar by_type={d.by_type || {}} />
                  {d.weakest_cards && d.weakest_cards.length > 0 && (
                    <div style={{
                      marginTop: 14, padding: 12,
                      background: '#FEF3C7', borderRadius: 8,
                      border: '1px solid #FCD34D',
                    }}>
                      <div style={{
                        fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                        fontWeight: 700, color: '#92400E', marginBottom: 6,
                      }}>Weakest cards in this deck</div>
                      {d.weakest_cards.map((c) => (
                        <div key={c.card_id} data-testid={`weakest-in-${d.deck_id}-${c.card_id}`}
                             style={{
                          padding: '6px 0', fontSize: 13, color: STUDY_THEME.inkSoft,
                          borderBottom: '1px dashed rgba(146,64,14,0.2)',
                        }}>
                          <b style={{ color: '#92400E' }}>{c.mastery.toFixed(0)}%</b>
                          {' · '}{c.front}
                          {' '}<span style={{ color: STUDY_THEME.muted, fontSize: 11 }}>
                            ({c.correct}/{c.attempts})
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: STUDY_THEME.muted }}>
              Loading topic health…
            </div>
          )
        )}

        {tab === 'students' && (
          <>
            {nudgeToast && (
              <div data-testid="nudge-toast" style={{
                position: 'fixed', top: 24, right: 24, zIndex: 50,
                background: 'white', padding: '12px 18px', borderRadius: 8,
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                border: `1px solid ${STUDY_THEME.line}`,
                fontSize: 13, fontWeight: 600, color: STUDY_THEME.ink,
              }}>{nudgeToast}</div>
            )}
            <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
              <label htmlFor="threshold" style={{
                fontSize: 13, fontWeight: 600, color: STUDY_THEME.inkSoft,
              }}>Mastery threshold:</label>
              <input
                id="threshold" type="number" min={0} max={100}
                data-testid="threshold-input"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                style={{
                  width: 70, padding: '6px 8px', fontSize: 13,
                  border: `1px solid ${STUDY_THEME.line}`, borderRadius: 6,
                  fontFamily: 'inherit',
                }}
              />
              <span style={{ fontSize: 13, color: STUDY_THEME.muted }}>%</span>
            </div>
            {students ? (
              <>
                <div style={cardStyle}>
                  <div style={{
                    fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                    fontWeight: 700, color: '#DC2626', marginBottom: 10,
                  }}>Below threshold — {students.struggling.length}</div>
                  {students.struggling.length === 0 ? (
                    <div style={{ color: STUDY_THEME.muted, fontSize: 13 }}>
                      Nobody is below {threshold}% mastery. Nice.
                    </div>
                  ) : students.struggling.map((s) => (
                    <div key={s.email} data-testid={`struggling-${s.email}`}
                         style={{
                           padding: '10px 0', fontSize: 13,
                           borderBottom: `1px solid ${STUDY_THEME.line}`,
                           display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto',
                           gap: 10, alignItems: 'center',
                         }}>
                      <div>
                        <div style={{ fontWeight: 700, color: STUDY_THEME.ink }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: STUDY_THEME.muted }}>{s.email}</div>
                      </div>
                      <div><b style={{ color: '#DC2626' }}>{s.recall_mastery.toFixed(0)}%</b> recall</div>
                      <div>{s.connect_strength.toFixed(0)}% connect</div>
                      <div>{s.streak_days}d streak</div>
                      <div>{s.cards_reviewed} cards</div>
                      <button
                        onClick={() => sendNudge(s)}
                        disabled={nudging === s.email}
                        data-testid={`nudge-${s.email}`}
                        style={{
                          padding: '6px 12px', fontSize: 12, fontWeight: 700,
                          background: '#F97316', color: 'white',
                          border: '1px solid #EA580C', borderRadius: 6,
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}
                        title="Send a personalised in-app nudge (once per 24h)"
                      >
                        {nudging === s.email ? '…' : '💌 Nudge'}
                      </button>
                    </div>
                  ))}
                </div>
                <div style={cardStyle}>
                  <div style={{
                    fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                    fontWeight: 700, color: '#16A34A', marginBottom: 10,
                  }}>Healthy — {students.healthy.length}</div>
                  {students.healthy.slice(0, 20).map((s) => (
                    <div key={s.email} data-testid={`healthy-${s.email}`}
                         style={{
                           padding: '10px 0', fontSize: 13,
                           borderBottom: `1px solid ${STUDY_THEME.line}`,
                           display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
                           gap: 10, alignItems: 'center',
                         }}>
                      <div>
                        <div style={{ fontWeight: 700, color: STUDY_THEME.ink }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: STUDY_THEME.muted }}>{s.email}</div>
                      </div>
                      <div><b style={{ color: '#16A34A' }}>{s.recall_mastery.toFixed(0)}%</b> recall</div>
                      <div>{s.connect_strength.toFixed(0)}% connect</div>
                      <div>{s.streak_days}d streak</div>
                      <div>{s.cards_reviewed} cards</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ padding: 40, textAlign: 'center', color: STUDY_THEME.muted }}>
                Loading students…
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
